import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { trigger, transition, style, animate, state, AnimationEvent } from '@angular/animations';
import { Observable } from 'rxjs';
import { DocumentQueryParams, FieldDataType, FilterGroup, TableFilter, View, Workflow } from '../../store/models';
import { Dataset, DatasetSubtype } from '../../store/models/dataset.model';
import { DataApiService } from 'src/app/modules/cop/services/data/data-api.service';
import { cloneDeep } from 'lodash-es';
@Component({
    selector: 'topo-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    standalone: false
})
export class TableFilterComponent {
  _dataset: Dataset;
  _tableFilter: TableFilter;

  @Input() set dataset(value: Dataset) {
    this._dataset = value;
    let filterView = value?.workflow?.views?.find(v => v.name === 'filter');
    if (!!!filterView) {
      filterView = this.defaultView;
    }
    this.buildFilterView(filterView);
  }

  @Input() set filters(value: TableFilter) {
    this._tableFilter = cloneDeep(value);
  }

  @Input() canSelectAll: boolean;

  @Input() defaultView: View;

  @Output() filterChanged = new EventEmitter<{ filter: TableFilter; allowSaveAll: boolean }>();

  filterFields: { systemName: string; name: string; expanded: boolean; loaded: boolean; values: { uId: string; name: string }[] }[];
  allowSaveAll: boolean;
  showSelectAllConfirmation: boolean;

  constructor(private dataApiService: DataApiService) {}

  expandCollapseField(systemName: string): void {
    const filterField = this.filterFields?.find(f => f.systemName === systemName);
    if (!!filterField) {
      filterField.expanded = !filterField.expanded;
      if (filterField.expanded === true && filterField.loaded !== true) {
        this.loadFilterFieldValues(filterField.systemName);
      }
    }
  }

  private loadFilterFieldValues(systemName: string): void {
    const filterField = this.filterFields?.find(f => f.systemName === systemName);
    const field = this._dataset?.schema?.fields.find(f => f.systemName === systemName);
    if (field.dataType === FieldDataType.Picklist || field.dataType === FieldDataType.MultiPicklist) {
      filterField.loaded = true;
      filterField.values = field.picklistValues?.map(p => {
        return { uId: p.uId, name: p.name };
      });
      return;
    }
    const documentQuery = {
      pagination: {
        skip: 0,
        take: 1000
      },
      filterGroup: null,
      fields: field.systemName,
      group: field.systemName
    } as DocumentQueryParams;

    const apiCall =
      this._dataset.subtype === DatasetSubtype.HorizonLocationIntelRatings ||
      this._dataset.subtype === DatasetSubtype.HorizonGeospatialThreatZone
        ? 'getHorizonIntelRatingsDocuments'
        : 'getDocuments';

    this.dataApiService[apiCall](this._dataset.uId, documentQuery, true).subscribe(resp => {
      filterField.loaded = true;
      filterField.values = [];
      resp?.data?.forEach(d => {
        if (!!d[field.systemName] && d[field.systemName].toString()?.trim()?.length) {
          filterField.values.push({ uId: d[field.systemName], name: d[field.systemName] });
        }
      });
    });
  }

  private buildFilterView(view: View): void {
    this.filterFields = [];

    this.allowSaveAll = false;

    view?.controls?.forEach(c => {
      const field = this._dataset?.schema?.fields.find(f => f.uId === c?.dataSetFieldUId);
      this.filterFields.push({
        systemName: field?.systemName,
        name: field?.displayName,
        expanded: false,
        loaded: false,
        values: []
      });
    });
  }

  public allowSaveAllChecked(): void {
    const allowSaveAll = this.allowSaveAll == true;
    if (!allowSaveAll && this._tableFilter?.filters?.length > 0) {
      this.showSelectAllConfirmation = true;
    } else {
      this.allowSaveAll = !allowSaveAll;
      this.filterChanged.emit({ filter: this._tableFilter, allowSaveAll: this.allowSaveAll });
    }
  }

  public getFilterValuesCount(systemName: string): string {
    const length = this._tableFilter?.filters?.find(f => f.systemName === systemName)?.values?.length;
    return !!length ? ' (' + length + ')' : null;
  }

  public onConfirmSelectAll(allow: boolean): void {
    this.showSelectAllConfirmation = false;
    if (allow) {
      const allowSaveAll = this.allowSaveAll == true;
      this.allowSaveAll = !allowSaveAll;
      if (!!!this._tableFilter) {
        this._tableFilter = {
          filters: []
        };
      }
      if (this.allowSaveAll === true) {
        this._tableFilter.filters = [];
      }
      this.filterChanged.emit({ filter: this._tableFilter, allowSaveAll: this.allowSaveAll });
    }
  }

  public getFilterChecked(systemName: string, valueUId: string): boolean {
    const filterValues = this._tableFilter?.filters?.find(f => f.systemName === systemName);
    return filterValues?.values?.some(v => v.uId == valueUId);
  }

  public setFilterChecked(systemName: string, valueUId: string): void {
    if (!!!this._tableFilter) {
      this._tableFilter = {
        filters: []
      };
    }

    if (!!!this._tableFilter.filters) {
      this._tableFilter.filters = [];
    }

    const filterField = this.filterFields?.find(f => f.systemName === systemName);
    const filterValue = filterField?.values?.find(v => v.uId === valueUId);

    const checked = !this.getFilterChecked(systemName, valueUId);
    if (checked === true) {
      let tableField = this._tableFilter?.filters?.find(f => f.systemName === systemName);
      if (!!!tableField) {
        tableField = {
          systemName: systemName,
          values: []
        };
        this._tableFilter?.filters?.push(tableField);
      }
      if (!!!tableField.values) {
        tableField.values = [];
      }
      tableField.values?.push({
        uId: filterValue.uId,
        name: filterValue.name
      });
      this.allowSaveAll = false;
    } else {
      let tableField = this._tableFilter?.filters?.find(f => f.systemName === systemName);
      if (!!tableField) {
        const index = tableField.values?.findIndex(v => v.uId === valueUId);
        if (index >= 0) {
          tableField.values.splice(index, 1);
        }
        if (tableField.values.length == 0) {
          tableField.values = null;
        }
      }
    }

    this.filterChanged.emit({ filter: this._tableFilter, allowSaveAll: this.allowSaveAll });
  }
}
